import { useEffect, useMemo, useState } from 'react';
import { reportError } from '~/utilities/errorReporting';
import type { Font } from '~/utilities/fonts';
import { getFontsFromDocument } from '~/utilities/fonts';
import { injectGoogleFonts } from '~/utilities/injectGoogleFonts';
import { isNil, isNotNil } from '~/utilities/type-guards';

type UseInjectFontsReturn = {
  customFontsLoadedSuccessfully: boolean;
  errorLoadingCustomFonts: boolean;
  isLoadingCustomFonts: boolean;
};

export const useInjectFonts = ({
  fonts,
  isWaitingForCustomizationsToLoad,
}: {
  isWaitingForCustomizationsToLoad: boolean;
  fonts: Font[];
}): UseInjectFontsReturn => {
  const customFonts = fonts.filter((font) => isNotNil(font.url));
  const googleFonts = fonts.filter((font) => isNil(font.url));
  const documentFonts = getFontsFromDocument();
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fontsToLoadWithFontFaceApi = useMemo(() => {
    return customFonts
      .filter(({ family }) => !documentFonts.has(family))
      .map(({ family, url }) => new FontFace(family, `url(${url})`));
  }, [customFonts, documentFonts]);

  const customFontsLoadedSuccessfully = customFonts.every(({ family }) => {
    return documentFonts.has(family);
  });

  injectGoogleFonts(googleFonts.map((font) => font.family));

  useEffect(() => {
    if (isWaitingForCustomizationsToLoad) {
      return;
    }

    const fontLoadingPromises = fontsToLoadWithFontFaceApi.map(async (font) => {
      document.fonts.add(font);
      return new Promise((resolve, reject) => {
        font
          .load()
          .then((fontFace: FontFace) => resolve(fontFace))
          .catch((error: unknown) => {
            if (error instanceof Error) {
              const fontUrl = customFonts.find(
                (customFont) => customFont.family === font.family,
              )?.url;
              reportError(error, {
                pillar: 'publish',
                product: 'brandkit',
                component: 'UploadedFont',
                description: 'Failed to load custom font',
                context: {
                  fontFamily: font.family,
                  fontUrl,
                },
              });
              reject(error);
            }
          });
      });
    });

    void Promise.allSettled(fontLoadingPromises).then((results) => {
      const rejectedResults = results.filter((result) => result.status === 'rejected');
      // We don't need this promise to return anything
      // eslint-disable-next-line promise/always-return
      if (rejectedResults.length > 0) {
        setHasError(true);
      }
      setIsLoading(false);
    });

    /* TODO: we should unload the fonts we've loaded, but as of right now when we refetch our loaded fonts
    /* after saving or deleting a font, the fonts are removed and reloaded, resulting in unnecessary processing
    /* and a flash of unstyled text.
    /*
    /* Task to figure this out: https://app.shortcut.com/wistia-pde/story/29454
     */
  }, [customFonts, fontsToLoadWithFontFaceApi, isWaitingForCustomizationsToLoad]);

  // Most of the time, the consumers of useInjectFonts should still render even if
  // one or more custom fonts fail to load.
  // However, we'll still expose `customFontsLoadedSuccessfully` and `errorLoadingCustomFonts`
  // in case any consumers want to block rendering or re-try when fonts fail to load.
  return {
    customFontsLoadedSuccessfully,
    errorLoadingCustomFonts: hasError,
    isLoadingCustomFonts: isLoading,
  };
};
